
import React, { FC, useEffect, useState } from "react";
import { useIsMobile } from "../../hooks/useIsMobile";

import { ReactComponent as Link } from 'src/assets/BookpairLink.svg';
import { ReactComponent as Time } from 'src/assets/BookpairTime.svg';
import { ReactComponent as Location } from 'src/assets/BookpairLocation.svg';

interface CalendarEvent {
    summary?: string;
    location?: string;
    description?: string;
    start?: { dateTime?: string; date?: string };
    end?: { dateTime?: string; date?: string };
}

interface CalendarViewerProps {
    events: CalendarEvent[];
    statusMessage: string;
    isLoading: boolean;
}

const formatTime = (date: Date | null): string | null => {
    if (!date) return null;
    return date.toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit", hour12: false });
};

const extractCity = (location: string): string => {
    if (!location) return "";
    const match = location.match(/([가-힣]+(?:특별시|광역시|시))/);
    return match ? match[1] : "";
};

const getEventStatus = (event: CalendarEvent): "upcoming" | "ongoing" | "ended" => {
    const now = new Date();
    const start = event.start?.dateTime ? new Date(event.start.dateTime) : event.start?.date ? new Date(event.start.date) : null;
    const end = event.end?.dateTime ? new Date(event.end.dateTime) : event.end?.date ? new Date(event.end.date) : null;
    if (!start) return "upcoming";
    if (now < start) return "upcoming";
    if (end && now > end) return "ended";
    return "ongoing";
};

// 프로젝트 컬러 기반 파스텔 뱃지
const StatusBadge: FC<{ status: "upcoming" | "ongoing" | "ended" }> = ({ status }) => {
    const config = {
        upcoming: {
            label: "진행예정",
            style: { background: "#FFF8E1", color: "#F9A825", border: "1px solid #FDEEC3" },
        },
        ongoing: {
            label: "진행중",
            style: { background: "#D1F5E4", color: "#0D894F", border: "1px solid #A3ECCA" },
        },
        ended: {
            label: "종료",
            style: { background: "#EFECEC", color: "#9F9693", border: "1px solid #E1DCDB" },
        },
    };
    const { label, style } = config[status];
    return (
        <span
            className="text-xs font-medium px-2 py-0.5 rounded-full"
            style={style}
        >
            {label}
        </span>
    );
};

const SkeletonCard: FC<{ index: number }> = ({ index }) => (
    <div
        className="bg-white border border-[#E1DCDB] rounded-xl p-6 flex flex-row gap-6 animate-pulse"
        style={{ animationDelay: `${index * 0.1}s` }}
    >
        <div className="flex flex-col items-center gap-2">
            <div className="w-14 h-14 bg-[#EFECEC] rounded-xl" />
            <div className="w-6 h-3 bg-[#EFECEC] rounded" />
        </div>
        <div className="flex flex-col flex-1 gap-3 pt-1">
            <div className="flex gap-2 items-center">
                <div className="h-5 bg-[#EFECEC] rounded w-48" />
                <div className="h-5 bg-[#EFECEC] rounded-full w-14" />
            </div>
            <div className="h-4 bg-[#FAF9F9] rounded w-full" />
            <div className="h-4 bg-[#FAF9F9] rounded w-3/4" />
            <div className="flex gap-4 mt-1">
                <div className="h-3 bg-[#FAF9F9] rounded w-32" />
                <div className="h-3 bg-[#FAF9F9] rounded w-24" />
            </div>
        </div>
    </div>
);

const EventCard: FC<{ event: CalendarEvent; idx: number }> = ({ event, idx }) => {
    const isMobile = useIsMobile();
    const [visible, setVisible] = useState(false);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setVisible(true), idx * 80);
        return () => clearTimeout(timer);
    }, [idx]);

    const start = event.start?.dateTime || event.start?.date || "";
    const dateObj = start ? new Date(start) : null;
    const DD = dateObj ? dateObj.getDate().toString().padStart(2, "0") : "";
    const MM = dateObj ? (dateObj.getMonth() + 1).toString().padStart(2, "0") : "";
    const weekdayNames = ["일", "월", "화", "수", "목", "금", "토"];
    const weekday = dateObj ? weekdayNames[dateObj.getDay()] : "";

    const startDate = event.start?.dateTime ? new Date(event.start.dateTime) : null;
    const endDate = event.end?.dateTime ? new Date(event.end.dateTime) : null;
    const startTime = formatTime(startDate);
    const endTime = formatTime(endDate);
    const time = startTime && endTime ? `${startTime} ~ ${endTime}` : startTime || "";

    let cleanDescription = "";
    let officialSiteUrl: string | null = null;
    if (event.description) {
        const processedText = event.description.replace(/<[^>]*>/g, '');
        const urlMatch = processedText.match(/https?:\/\/[^\s,]+/);
        if (urlMatch) officialSiteUrl = urlMatch[0];
        const parts = processedText.split("****");
        if (parts.length > 1) {
            cleanDescription = parts[1].split(/\s+/).filter(Boolean).join(" ").trim();
        } else {
            let textWithoutUrl = processedText;
            if (officialSiteUrl) {
                textWithoutUrl = processedText.replace(officialSiteUrl, "");
            }
            cleanDescription = textWithoutUrl.split(/\s+/).filter(Boolean).join(" ").trim();
        }
    }

    const status = getEventStatus(event);
    const city = event.location ? extractCity(event.location) : "";
    // 뱃지는 광역시/특별시 제거한 짧은 이름으로 표시
    const cityShort = city.replace(/특별시|광역시|시$/, "");
    const isEnded = status === "ended";

    // 설명이 길어서 더보기가 필요한지 판단 (약 80자 기준)
    const isLongDescription = cleanDescription.length > 80;

    return (
        <div
            className="group"
            style={{
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(12px)",
                transition: "opacity 0.4s ease, transform 0.4s ease",
            }}
        >
            <div
                className={`
                    bg-white border border-[#E1DCDB] rounded-xl flex flex-col
                    hover:shadow-book hover:-translate-y-0.5
                    transition-all duration-300 ease-out
                    ${isEnded ? "opacity-60" : ""}
                    ${isMobile ? "p-4" : "p-6"}
                `}
            >
                <div className="flex flex-row gap-5">
                    {/* 날짜 섹션 */}
                    <div className={`flex flex-col items-center justify-center gap-0.5 ${isMobile ? "min-w-[36px]" : "min-w-[48px]"}`}>
                        <span className={`font-bold leading-none ${isMobile ? "text-[24px]" : "text-4xl"} ${isEnded ? "text-grey-30" : "text-main"}`}>
                            {DD}
                        </span>
                        <span className={`text-sm font-normal ${isEnded ? "text-grey-30" : "text-grey-50"}`}>
                            {weekday}
                        </span>
                    </div>

                    {/* 구분선 */}
                    <div className={`w-px self-stretch ${isEnded ? "bg-[#E1DCDB]" : "bg-primary-40"} group-hover:bg-primary-80 transition-colors duration-300`} />

                    {/* 내용 섹션 */}
                    <div className={`flex flex-col flex-1 ${isMobile ? "gap-1.5" : "gap-2"}`}>
                        {/* 모바일: 뱃지 → 타이틀 순서 */}
                        {isMobile && (
                            <div className="flex flex-row items-center gap-1.5">
                                <StatusBadge status={status} />
                                {cityShort && (
                                    <span
                                        className="text-xs font-medium px-2 py-0.5 rounded-full"
                                        style={{ background: "#D1F5E4", color: "#0D894F", border: "1px solid #A3ECCA" }}
                                    >
                                        {cityShort}
                                    </span>
                                )}
                            </div>
                        )}

                        {/* 타이틀 */}
                        <span className={`font-semibold ${isMobile ? "text-sm" : "text-lg"} text-grey-90 leading-snug`}>
                            {event.summary || "(제목 없음)"}
                        </span>

                        {/* 설명 + 더보기/접기 */}
                        {cleanDescription && (
                            <div>
                                <p className={`text-grey-50 leading-relaxed ${isMobile ? "text-xs" : "text-sm"} ${expanded ? "" : "line-clamp-2"}`}>
                                    {cleanDescription}
                                </p>
                                {isLongDescription && (
                                    <button
                                        onClick={() => setExpanded(!expanded)}
                                        className="text-xs text-main font-medium mt-0.5 hover:underline"
                                    >
                                        {expanded ? "접기" : "더보기"}
                                    </button>
                                )}
                            </div>
                        )}

                        {/* 장소 / 시간 */}
                        <div className={`flex flex-wrap gap-x-4 gap-y-1 ${isMobile ? "mt-0.5" : "mt-1"}`}>
                            {event.location && (
                                <div className="flex flex-row gap-1 items-center">
                                    <Location className="w-3.5 h-3.5 shrink-0" />
                                    <span className={`text-grey-50 font-normal ${isMobile ? "text-xs" : "text-sm"}`}>
                                        {event.location}
                                    </span>
                                </div>
                            )}
                            {time && (
                                <div className="flex flex-row gap-1 items-center">
                                    <Time className="w-3.5 h-3.5 shrink-0" />
                                    <span className={`text-grey-50 font-normal ${isMobile ? "text-xs" : "text-sm"}`}>
                                        {time}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* PC: 뱃지 + 링크버튼 우측 수직 배치 */}
                    {!isMobile && (
                        <div className="flex flex-col items-end gap-2 shrink-0">
                            <div className="flex flex-row items-center gap-1.5">
                                <StatusBadge status={status} />
                                {cityShort && (
                                    <span
                                        className="text-xs font-medium px-2 py-0.5 rounded-full"
                                        style={{ background: "#D1F5E4", color: "#0D894F", border: "1px solid #A3ECCA" }}
                                    >
                                        {cityShort}
                                    </span>
                                )}
                            </div>
                            <button
                                onClick={() => officialSiteUrl && window.open(officialSiteUrl, "_blank", "noopener,noreferrer")}
                                disabled={!officialSiteUrl}
                                className={`
                                    w-9 h-9 rounded-lg flex justify-center items-center
                                    border border-[#E1DCDB] bg-white
                                    transition-all duration-200
                                    ${officialSiteUrl
                                        ? "hover:bg-primary-20 hover:border-primary-80 cursor-pointer"
                                        : "opacity-30 cursor-not-allowed"
                                    }
                                `}
                            >
                                <Link />
                            </button>
                        </div>
                    )}
                </div>

                {/* 모바일 링크 버튼 */}
                {isMobile && (
                    <button
                        onClick={() => officialSiteUrl && window.open(officialSiteUrl, "_blank", "noopener,noreferrer")}
                        disabled={!officialSiteUrl}
                        className={`
                            flex flex-row gap-1.5 w-full h-8 mt-3 rounded-lg
                            border border-[#E1DCDB] bg-[#FAF9F9] justify-center items-center
                            transition-all duration-200
                            ${officialSiteUrl
                                ? "hover:bg-primary-20 hover:border-primary-80 cursor-pointer"
                                : "opacity-30 cursor-not-allowed"
                            }
                        `}
                    >
                        <Link className="w-3 h-3" />
                        <span className="text-xs font-medium text-grey-70">공식 홈페이지</span>
                    </button>
                )}
            </div>
        </div>
    );
};

type StatusFilter = "active" | "all" | "upcoming" | "ongoing" | "ended";

const STATUS_FILTER_OPTIONS: { value: StatusFilter; label: string }[] = [
    { value: "active", label: "전체" },
    { value: "ongoing", label: "진행중" },
    { value: "upcoming", label: "진행예정" },
];

const FilterButton: FC<{
    label: string;
    active: boolean;
    onClick: () => void;
    color?: string;
}> = ({ label, active, onClick, color = "#19CF7A" }) => (
    <button
        onClick={onClick}
        className="whitespace-nowrap text-sm font-medium px-4 py-1.5 rounded-full border transition-all duration-200"
        style={active
            ? { background: color, color: "#fff", borderColor: color }
            : { background: "#fff", color: "#7F7471", borderColor: "#E1DCDB" }
        }
    >
        {label}
    </button>
);

const CalendarViewer: FC<CalendarViewerProps> = ({ events, statusMessage, isLoading }) => {
    const isMobile = useIsMobile();
    const [statusFilter, setStatusFilter] = useState<StatusFilter>("active");
    const [regionFilter, setRegionFilter] = useState<string>("all");

    // 이벤트에서 고유 도시 추출
    const cities = Array.from(
        new Set(
            events
                .map(e => e.location ? extractCity(e.location) : "")
                .filter(Boolean)
        )
    ).sort();

    // 필터 적용
    const filteredEvents = events.filter(event => {
        const eventStatus = getEventStatus(event);
        let statusMatch: boolean;
        if (statusFilter === "active") {
            statusMatch = eventStatus === "upcoming" || eventStatus === "ongoing";
        } else if (statusFilter === "all") {
            statusMatch = true;
        } else {
            statusMatch = eventStatus === statusFilter;
        }
        const city = event.location ? extractCity(event.location) : "";
        const regionMatch = regionFilter === "all" || city === regionFilter;
        return statusMatch && regionMatch;
    });

    const groupedByYear = filteredEvents.reduce((acc, event) => {
        const start = event.start?.dateTime || event.start?.date || null;
        if (!start) return acc;
        const date = new Date(start);
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        if (!acc[year]) acc[year] = {};
        if (!acc[year][month]) acc[year][month] = [];
        acc[year][month].push(event);
        return acc;
    }, {} as Record<number, Record<string, CalendarEvent[]>>);

    const sortedYears = Object.keys(groupedByYear).map(Number).sort((a, b) => a - b);

    let cardIndex = 0;

    return (
        <div className={`w-full mx-auto ${isMobile ? "px-4" : "max-w-[1200px] px-6"}`}>

            {/* 필터 영역 - 한 줄 compact */}
            {!isLoading && !statusMessage && events.length > 0 && (
                <div className="flex flex-row items-center justify-between gap-3 mb-6 flex-wrap">
                    {/* 상태 필터 - 필 버튼 */}
                    <div className="flex flex-row gap-1.5">
                        {STATUS_FILTER_OPTIONS.map(opt => (
                            <FilterButton
                                key={opt.value}
                                label={opt.label}
                                active={statusFilter === opt.value}
                                onClick={() => setStatusFilter(opt.value)}
                            />
                        ))}
                    </div>

                    {/* 지역 필터 - 드롭다운 */}
                    {cities.length > 0 && (
                        <select
                            value={regionFilter}
                            onChange={e => setRegionFilter(e.target.value)}
                            className={`
                                border border-[#E1DCDB] rounded-full bg-white text-grey-70 font-medium
                                px-4 pr-8 py-1.5 appearance-none cursor-pointer
                                focus:outline-none focus:border-main
                                transition-colors duration-200
                                ${isMobile ? "text-xs" : "text-sm"}
                            `}
                            style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239F9693' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center" }}
                        >
                            <option value="all">전국</option>
                            {cities.map(city => (
                                <option key={city} value={city}>{city}</option>
                            ))}
                        </select>
                    )}
                </div>
            )}

            {isLoading ? (
                <div className="flex flex-col gap-4">
                    {[0, 1, 2].map(i => <SkeletonCard key={i} index={i} />)}
                </div>
            ) : statusMessage ? (
                <div className="flex flex-col items-center justify-center py-20 gap-3">
                    <span className="text-4xl">📅</span>
                    <span className="text-grey-40 text-sm">{statusMessage}</span>
                </div>
            ) : filteredEvents.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 gap-3">
                    <span className="text-4xl">📅</span>
                    <span className="text-grey-40 text-sm">해당하는 북페어 일정이 없습니다.</span>
                </div>
            ) : (
                <div className="flex flex-col gap-10">
                    {sortedYears.map((year) => (
                        <div key={year} className="flex flex-col gap-8">
                            {/* 연도 헤더 */}
                            <div className="flex items-center gap-3">
                                <span className={`font-bold text-grey-90 ${isMobile ? "text-xl" : "text-3xl"}`}>
                                    {year}
                                </span>
                                <div className="flex-1 h-px bg-[#E1DCDB]" />
                            </div>

                            {Object.keys(groupedByYear[year]).sort().map((month) => (
                                <div key={month} className="flex flex-col gap-4">
                                    {/* 월 헤더 */}
                                    <div className="flex items-center gap-2">
                                        <div className="w-1.5 h-5 rounded-full bg-main" />
                                        <h3 className={`font-semibold text-grey-70 ${isMobile ? "text-base" : "text-xl"}`}>
                                            {month}월
                                        </h3>
                                        <span className="text-xs text-grey-40 font-normal">
                                            ({groupedByYear[year][month].length}개)
                                        </span>
                                    </div>

                                    {/* 카드 목록 */}
                                    <div className={`flex flex-col gap-3 ${isMobile ? "" : "pl-4"}`}>
                                        {groupedByYear[year][month].map((event) => (
                                            <EventCard key={cardIndex} event={event} idx={cardIndex++} />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default CalendarViewer;
